import { Card, Divider, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  callAPI,
  clearRequest,
  Method
} from "../../commonFunctions/CommonFunctions";
import { WSIcons } from "../../commonFunctions/iIcons";
import WSButton from "../../Components/Button/WSButton";
import WSDateField from "../../Components/DateField/WSDateField";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";
import WSSelectField from "../../Components/Select/WSSelect";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSTextField, {
  CurrencyType,
  TextType
} from "../../Components/TextBox/WSTextBox";
import { WSView } from "../../Components/View/WSView";
import WSTextAreaField from "../../Components/WSTextArea/WSTextArea";
import config from "../../Config/clientConfig.json";

export const Estimation: React.FC<any> = React.memo((props) => {
  const { id, estId } = useParams();
  const [loading, setLoading] = useState(false)
  const { control, register, handleSubmit, setValue, watch, getValues } =
    useForm({
      defaultValues: {
        ownerId: localStorage.getItem("UserId"),
        customerName: "",
        subject: "",
        date: "",
        expiredate: "",
        address: "",
        stage: "",
        discount: "",
        adjustments: "",
        termsAndConditions: "",
        remarks: "",
        leadId: "",
        items: [
          { productId: "", quantity: "", unit: "", unitPrice: "", total: "" }
        ]
      }
    });
  const { fields, append, remove, replace } = useFieldArray({
    name: "items",
    control
  });
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const [products, setProducts] = useState<any>([]);
  const [lead, setLead] = useState<any>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    calculateTotal(subtotal);
  }, [subtotal, watch("discount"), watch("adjustments")]);

  const calculateTotal = (newSubtotal: number) => {
    const discount = parseFloat(watch("discount")) || 0;
    const adjustments = parseFloat(watch("adjustments")) || 0;
    const newTotal = newSubtotal - discount + adjustments;
    setTotal(newTotal);
  };

  // Fetch product details on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response: any = await callAPI("products", Method.GET);
        if (response) {
          setProducts(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response: any = await callAPI("leads", Method.GET);
        if (response) {
          setLead(response);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    fetchLeads();
  }, []);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const response: any = await callAPI("estimates", Method.GET, {}, estId);

        if (response?.data) {
          // Set main form values
          setValue("ownerId", response.data.ownerId);
          setValue("customerName", response.data.customerName);
          setValue("address", response.data.address);
          setValue("subject", response.data.subject);
          setValue("stage", response.data.stage);
          setValue("discount", response.data.discount);
          setValue("adjustments", response.data.adjustments);
          setValue("termsAndConditions", response.data.termsAndConditions);
          setValue("remarks", response.data.remarks);
          setValue("leadId", response.data.leadId);

          // Format dates
          setValue("date", dayjs(response.data.date).format("MM-DD-YYYY"));
          setValue(
            "expiredate",
            dayjs(response.data.validTill).format("MM-DD-YYYY")
          );

          setSubtotal(response.data.subtotal);
          setTotal(response.data.grandTotal);

          // Populate items
          if (response.data.estimateItems?.length) {
            const formattedItems = response.data.estimateItems.map(
              (item: any) => ({
                productId: item.productId,
                quantity: item.quantity.toString(),
                unit: item.unit,
                unitPrice: item.unitPrice.toString()
              })
            );

            replace(formattedItems); // Updates useFieldArray fields
          }
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    if (estId) fetchEstimate();
  }, [estId, setValue, replace]);

  const handleData = async (data: any) => {
    try {
      let request = {
        ownerId: localStorage.getItem("UserId"),
        customerName: data?.customerName || "",
        address: data?.address || "",
        subject: data?.subject || "",
        stage: data?.stage || "Draft",
        date: dayjs(data.date).format("YYYY-MM-DDTHH:mm:ss.SSS"),
        validTill: dayjs(data.expiredate).format("YYYY-MM-DDTHH:mm:ss.SSS"),
        termsAndConditions: data?.termsAndConditions || "",
        remarks: data?.remarks || "demo",
        leadId: id || "",
        subtotal: Number(subtotal) || 0,
        discount: Number(data?.discount) || 0,
        adjustments: Number(data?.adjustments) || 0,
        grandTotal: Number(total) || 0,
        items: data?.items || []
      };
      const newRequest = clearRequest(request);
      setLoading(true)
      if (estId) {
        const response: any = await callAPI('estimates', Method.PUT, newRequest, estId);
        if (response) {
          showMessage(
            response?.message,
            response.success === true ? "success" : "error"
          );

          response.success === true && navigate(`/contact/edit/${id}`);
        }
      }
      else {
        const response: any = await callAPI('estimates', Method.POST, newRequest);
        if (response) {
          showMessage(
            response?.message,
            response.success === true ? "success" : "error"
          );

          response.success === true && navigate(`/contact/edit/${id}`);
        }
      }
    } catch (e: any) {
      showMessage(e.error);
    }
    finally {
      setLoading(false)
    }
  };

  const handleProductChange = (index: number, event: any) => {
    const selectedProductId = event.target.value; // Get selected product ID
    const selectedProduct = products.find(
      (p: any) => p.id === selectedProductId
    );
    if (selectedProduct) {
      setValue(`items.${index}.unit`, selectedProduct.unit);
      setValue(`items.${index}.unitPrice`, selectedProduct.unitPrice);

      setValue(`items.${index}.quantity`, "1"); // Convert 1 to string
      setValue(
        `items.${index}.total`,
        (selectedProduct.unitPrice * 1).toString()
      ); // Convert total to string

      // Recalculate Subtotal
      recalculateSubtotal();
    }
  };

  const recalculateSubtotal = () => {
    const updatedItems = getValues("items") || [];
    let newSubtotal = updatedItems.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 1;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return sum + quantity * unitPrice;
    }, 0);

    setSubtotal(newSubtotal);
    calculateTotal(newSubtotal);
  };

  const handleQuantityChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(`items.${index}.quantity`, event.target.value);

    setValue(`items.${index}.total`, total.toString()); // Convert to string

    // Recalculate Subtotal
    recalculateSubtotal();
  };

  const handleUnitPriceChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(`items.${index}.unitPrice`, event.target.value);
    setValue(`items.${index}.total`, total.toString()); // Convert to string

    // Recalculate Subtotal
    recalculateSubtotal();
  };

  const handleCancelClick = () => {
    navigate(`/contact/edit/${id}`)
  };
  const actions: IHeaderListActions[] =
    [
      { type: 'button', text: 'Cancel', size: 'medium', onClick: handleCancelClick, variant: 'outlined', color: 'primary' },
      { type: 'button', text: 'Save', loading: loading, size: 'medium', onClick: handleSubmit(handleData), buttonType: 'submit', variant: 'outlined', color: 'primary' }
    ]
  return (
    <Grid p={2}>
      <form onSubmit={handleSubmit(handleData)}>
        <HeaderList title={estId ? "Estimation > Update New Estimation" : "Estimation > Create New Estimation"} type="card" actions={actions} isBackButton url={`/contact/edit/${id}`} />
        {/* <Card sx={{ borderRadius: "10px", padding: 4, position: "relative" }}>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "space-between", // Ensures buttons are on both left and right
              alignItems: "center",
              position: "absolute",
              top: 16,
              left: 16,
              right: 16
            }}
          >
            <HeaderList title={estId ? "Estimation > Update New Estimation" : "Estimation > Create New Estimation"} type="card" actions={actions} isBackButton url={`contact/edit/${id}`} />
      
            <Grid sx={{ display: "flex", gap: 2 }}>
              <Typography fontWeight={"bold"} sx={{ color: "#7D79C2" }}>
                Estimation
              </Typography>
              <WSIcons.ChevronRightIcon />
              <Typography fontWeight={"bold"} sx={{ color: "#7D79C2" }}>
                {" "}
                {estId ? "Update New Estimation" : "Create New Estimation"}
              </Typography>
            </Grid>

         
            <Grid sx={{ display: "flex", gap: 2 }}>
              <WSButton
                id="cancel"
                label={"Cancel"}
                type="button"
                size="small"
                className="ws-cancel"
                variant="contained"
                onClick={handleCancelClick}
              />

              <WSButton
                id="submit"
                label={"Save"}
                onClick={handleSubmit(handleData)}
                type="submit"
                size="small"
                variant="contained"
                sx={{ backgroundColor: "#7D79C2" }}
              />
            </Grid>
          </Grid>
        </Card> */}
        <Card
          sx={{ borderRadius: "10px", padding: 3, position: "relative", mt: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight={"bold"}> Estimation</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <WSTextField
                label={"Customer Name *"}
                rules={{ required: true }}
                id={"customerName"}
                name={"customerName"}
                control={control}
              />
            </Grid>
            <Grid item xs={10} sm={6}>
              <WSSelectField
                rules={{ required: false }}
                {...register("stage")}
                id="stage"
                control={control}
                defaultValue={"0"}
                label="Stage"
                name="stage"
                options={config.stageConfig.staticOptionData}
              />
            </Grid>

            {/* <Grid item xs={12} sm={2}>
              <WSIcons.SearchIcon />
            </Grid> */}
            <Grid item xs={10} sm={12}>
              <WSTextField
                label={"Address *"}
                {...register("address")}
                id="address"
                name="address"
                control={control}
                rules={{ required: true }}
              />
            </Grid>


            <Grid item xs={10} sm={6}>
              <WSDateField
                id="date"
                name="date"
                label="Date *"
                control={control}
                rules={{ required: true }}
              />
            </Grid>
            <Grid item xs={10} sm={6}>
              <WSDateField
                id="expiredate"
                name="expiredate"
                label="Expiry Date"
                control={control}
                rules={{ required: true }}
              />
            </Grid>

            {/* <Grid item xs={12} sm={4}>
              <WSSelectField
                id="leadId"
                label={"Select Lead"}
                name="leadId"
                control={control}
                rules={{ required: "please select a lead" }}
                options={lead.map((value: any) => ({
                  label: value.firstName,
                  value: value.id
                }))}
              />
            </Grid> */}

            <Grid item xs={10} sm={12}>
              <WSTextAreaField
                label={"Subject"}
                id={"subject"}
                name={"subject"}
                control={control}
                rules={{ required: false }}
                rows={4}
              />
            </Grid>

            <Grid item xs={12} mt={2}>
              <Divider />
            </Grid>

            {/* Section Title */}
            <Grid item xs={12}>
              <Typography variant="h6">Items</Typography>
            </Grid>

            {/* Dynamic Item Fields */}

            {fields.map((row, index) => (
              <Grid
                container
                spacing={2}
                key={row.id}
                alignItems="center"
                mt={1}
                ml={1}
              >
                <Grid item xs={2}>
                  <WSSelectField
                    label="Select a Product"
                    rules={{ required: true }}
                    {...register(`items.${index}.productId`)}
                    id={`selectProduct-${index}`}
                    control={control}
                    options={products.map((product: any) => ({
                      label: product.name,
                      value: product.id
                    }))}
                    onChange={(event) => handleProductChange(index, event)}
                  />
                </Grid>

                <Grid item xs={1.5}>
                  <WSTextField
                    label="Unit"
                    {...register(`items.${index}.unit`)}
                    id={`unit-${index}`}
                    control={control}
                    disabled
                  />
                </Grid>

                <Grid item xs={2}>
                  <WSTextField
                    label="Unit Price"
                    {...register(`items.${index}.unitPrice`, {
                      onChange: (event) => handleUnitPriceChange(index, event)
                    })}
                    id={`unitPrice-${index}`}
                    control={control}
                    type={TextType.Currency}
                    currency={CurrencyType.Rupee}
                  />
                </Grid>

                <Grid item xs={2}>
                  <WSTextField
                    id={`quantity-${index}`}
                    label="Quantity"
                    {...register(`items.${index}.quantity`, {
                      onChange: (event) => handleQuantityChange(index, event)
                    })}
                    control={control}
                    type={TextType.Number}
                  />
                </Grid>

                {/* Total Amount for each product*/}
                <Grid item xs={1}>
                  <Typography variant="body1" fontWeight="bold">
                    ₹
                    {Number(watch(`items.${index}.unitPrice`) || 0) *
                      Number(watch(`items.${index}.quantity`) || 0)}
                  </Typography>
                </Grid>

                <Grid item display="flex" justifyContent="center">
                  <WSIcons.DeleteIcon
 onClick={() => {
      remove(index);
      setTimeout(() => {
        recalculateSubtotal();
      }, 0);
    }}                          sx={{ color: "red", cursor: 'pointer' }}
                  />
                </Grid>
              </Grid>
            ))}


            <Grid item xs={12} sx={{ marginLeft: 1 }}>
              <WSButton
                id="addButton"
                label="+ Add"
                size="small"
                onClick={() =>
                  append({
                    productId: "",
                    quantity: "",
                    unit: "",
                    unitPrice: "",
                    total: ""
                  })
                }
              />
            </Grid>

            {/* Terms & Conditions */}
            <Grid item xs={12} sm={12}>
              <WSTextAreaField
                id="termsandcondition"
                label="Terms and Conditions"
                {...register("termsAndConditions")}
                control={control}
                rows={3}
              />
            </Grid>

            {/* Summary Section */}
            <Grid
              container
              sx={{ backgroundColor: "#e8f0f0", p: 2, mt: 2, ml: 2 }}
              spacing={2}
            >
              {/* Subtotal */}

              <Grid item xs={8}>
                <WSView title="Sub Total" />
              </Grid>
              <Grid item xs={4} sx={{ textAlign: "right" }}>
                <WSView title={`₹ ${subtotal.toFixed(2)}`} />
              </Grid>

              {/* Discount */}
              <Grid item xs={6}>
                <WSView title="Discount" />
              </Grid>
              <Grid item xs={6}>
                <WSTextField
                  id="discount"
                  label="Discount"
                  {...register("discount")}
                  control={control}
                  type={TextType.Number}
                />
              </Grid>

              {/* Adjustments */}
              <Grid item xs={6}>
                <WSView title="Adjustments" />
              </Grid>
              <Grid item xs={6}>
                <WSTextField
                  id="adjustments"
                  label="Adjustments"
                  {...register("adjustments")}
                  control={control}
                  type={TextType.Number}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Total */}
              <Grid item xs={8}>
                <WSView title="Total ( ₹ )" />
              </Grid>
              <Grid item xs={4} sx={{ textAlign: "right" }}>
                <WSView title={`₹ ${total.toFixed(2)}`} />
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </form>
    </Grid>
  );
});
